import React, { MutableRefObject } from 'react';

import { Accept, ErrorCode, useDropzone } from 'react-dropzone';

import FileUploadContent from './FileUploadContent';
import IntegrationBar from './IntegrationBar';
import { useFileUploadLogic } from './useFileUploadLogic';
import { getErrorMessage } from './utils';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import RelativityIntegrationModal from '@/components/molecules/Modals/RelativityIntegrationModal';
import { ActiveJob } from '@/hooks/useJobProgress';

interface FileUploaderProps {
  isUploading: boolean;
  onDrop: (acceptedFiles: File[]) => void;
  successfulUploads?: number;
  totalProgress?: number;
  acceptedTypes?: Accept;
  maxFiles?: number;
  maxSize?: number;
  showPreview?: boolean;
  type?: string;
  caseId?: string;
  onApideckSuccess?: () => void;
  isApideckEnabled?: boolean;
  uploadIdRef?: MutableRefObject<string | null>;
  activeJobs?: ActiveJob[];
}

const FileUploadInput: React.FC<FileUploaderProps> = ({
  isUploading,
  onDrop,
  acceptedTypes,
  maxFiles,
  maxSize,
  totalProgress,
  showPreview,
  type = 'files',
  caseId,
  onApideckSuccess,
  successfulUploads = 1,
  isApideckEnabled,
  uploadIdRef,
  activeJobs,
}) => {
  const {
    files,
    localError,
    handleFileAccepted,
    onDeleteFile,
    modalOpen,
    setModalOpen,
    modalContent,
    isRelativityModalOpen,
    setIsRelativityModalOpen,
    apiDeckSessionToken,
    apiDeckConnections,
    connectRelativity,
    handleOpenApideckModal,
    handleSelectRelativityList,
  } = useFileUploadLogic({
    caseId,
    isApideckEnabled,
    maxFiles,
    onApideckSuccess,
    activeJobs,
  });

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    acceptedFiles,
    fileRejections = [],
  } = useDropzone({
    multiple: maxFiles !== 1,
    noClick: maxFiles === files.length,
    onDropAccepted: (acceptedFiles) => handleFileAccepted(acceptedFiles, onDrop),
    accept: acceptedTypes,
    maxSize,
    maxFiles,
  });

  const handleApideckModal = (connection: string, modalContent: React.ReactNode) => {
    handleOpenApideckModal(connection, modalContent);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      {caseId && (
        <IntegrationBar
          caseId={caseId}
          isApideckEnabled={isApideckEnabled}
          apiDeckConnections={apiDeckConnections}
          apiDeckSessionToken={apiDeckSessionToken}
          onRelativityConnect={connectRelativity}
          onApideckModal={handleApideckModal}
          onApideckSuccess={onApideckSuccess}
        />
      )}

      <div
        data-testid="file-uploader"
        className={`relative mt-2 flex max-h-56 min-h-40 items-center justify-center rounded-lg border-2 border-dashed bg-slate-50 ${
          maxFiles === files.length ? 'cursor-default' : 'cursor-pointer'
        }`}
        {...getRootProps()}
      >
        <input {...getInputProps()} data-testid="upload-files-input" />
        <FileUploadContent
          isUploading={isUploading}
          isDragActive={isDragActive}
          maxFiles={maxFiles}
          showPreview={showPreview}
          files={files}
          acceptedFiles={acceptedFiles}
          totalProgress={totalProgress}
          successfulUploads={successfulUploads}
          onDeleteClick={onDeleteFile}
          type={type}
        />
        <div className="absolute bottom-2 left-3">
          <p
            className={`h-4 text-xs text-red-400 transition-opacity ${
              fileRejections[0] || localError ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {getErrorMessage({
              errorCode: localError || (fileRejections[0]?.errors[0].code as ErrorCode),
              acceptedFileTypes: acceptedTypes,
              maxFiles,
              maxSize,
            })}
          </p>
        </div>
      </div>

      <CustomModal isOpen={modalOpen} handleClose={handleCloseModal} content={modalContent} maxWidth="52rem" />


      <CustomModal
        isOpen={isRelativityModalOpen}
        handleClose={() => setIsRelativityModalOpen(false)}
        content={
          <RelativityIntegrationModal
            caseId={caseId ?? ''}
            onSelectList={handleSelectRelativityList}
            onClose={() => setIsRelativityModalOpen(false)}
          />
        }
        maxWidth="40rem"
      />
    </>
  );
};

export default FileUploadInput;
